import { ZERO_AMOUNT, operatingIncome, sumColumns, sumMonths, } from './viewModel.js';
export const BUDGET_YEAR_OFFSET = 1;
function adjustLine(line, edit, expenseInflationRate) {
    const growth = line.direction === 'expense' ? 1 + expenseInflationRate : 1;
    const fixedBudgetYear = line.fixedScheduled?.[BUDGET_YEAR_OFFSET] ?? 0;
    const remainder = edit.amount - fixedBudgetYear;
    const years = line.years.map((year, offset) => {
        if (offset < BUDGET_YEAR_OFFSET ||
            (offset > BUDGET_YEAR_OFFSET && remainder < 0 && fixedBudgetYear > 0))
            return year;
        const target = (line.fixedScheduled?.[offset] ?? 0) + remainder * growth ** (offset - BUDGET_YEAR_OFFSET);
        const projected = year.actual + year.scheduled + year.inflation;
        return { ...year, adjustment: target - projected };
    });
    const months = line.months.map((series, offset) => series === undefined
        ? undefined
        : spreadAdjustment(series, years[offset]?.adjustment ?? 0, line.fixedScheduledMonths?.[offset]));
    return { ...line, years, months };
}
const projectedPart = (amount) => amount.actual + amount.scheduled + amount.inflation;
function spreadAdjustment(months, adjustment, fixedMonths) {
    const weights = months.map((month, index) => projectedPart(month) - (fixedMonths?.[index] ?? 0));
    const projectedYear = weights.reduce((sum, amount) => sum + amount, 0);
    return months.map((month, index) => ({
        ...month,
        adjustment: projectedYear === 0
            ? adjustment / months.length
            : (adjustment * (weights[index] ?? 0)) / projectedYear,
    }));
}
function regroup(groups, width) {
    return groups.map((group) => ({
        ...group,
        years: sumColumns(group.lines, width),
        months: sumMonths(group.lines, width),
    }));
}
export function applyBudget(model, edits, expenseInflationRate) {
    if (edits.size === 0)
        return model;
    const width = model.fiscalYears.length;
    const edit = (groups) => regroup(groups.map((group) => ({
        ...group,
        lines: group.lines.map((line) => {
            const lineEdit = edits.get(line.id);
            return lineEdit === undefined ? line : adjustLine(line, lineEdit, expenseInflationRate);
        }),
    })), width);
    const income = edit(model.income);
    const expense = edit(model.expense);
    return {
        ...model,
        income,
        expense,
        totals: {
            income: sumColumns(operatingIncome(income), width),
            expense: sumColumns(expense, width),
        },
        monthTotals: {
            income: sumMonths(operatingIncome(income), width),
            expense: sumMonths(expense, width),
        },
    };
}
export const ONE_TIME_GROUP_LABEL = 'Accepted inspection findings';
export function addOneTimeLines(model, items) {
    if (items.length === 0)
        return model;
    const width = model.fiscalYears.length;
    const blank = () => ({ ...ZERO_AMOUNT });
    const lines = items.map((item) => {
        const months = model.monthsOf.map((calendar, offset) => calendar === undefined
            ? undefined
            : calendar.map((_, m) => {
                const amount = blank();
                if (offset === BUDGET_YEAR_OFFSET) {
                    amount.adjustment =
                        item.dueMonth === undefined
                            ? item.amount / 12
                            : m + 1 === item.dueMonth
                                ? item.amount
                                : 0;
                }
                return amount;
            }));
        const years = model.fiscalYears.map((_, offset) => ({
            ...blank(),
            adjustment: offset === BUDGET_YEAR_OFFSET ? item.amount : 0,
        }));
        return {
            id: item.id,
            label: item.label,
            direction: 'expense',
            status: 'mapped',
            years,
            months,
        };
    });
    const group = {
        id: 'expense:one-time',
        label: ONE_TIME_GROUP_LABEL,
        direction: 'expense',
        lines,
        years: sumColumns(lines, width),
        months: sumMonths(lines, width),
    };
    const expense = [...model.expense, group];
    return {
        ...model,
        expense,
        totals: { ...model.totals, expense: sumColumns(expense, width) },
        monthTotals: { ...model.monthTotals, expense: sumMonths(expense, width) },
    };
}
export function editsMissingBasis(edits) {
    return [...edits.entries()]
        .filter(([, edit]) => edit.basis.trim() === '')
        .map(([lineId]) => lineId);
}
export function unplacedLines(model) {
    return [...model.income, ...model.expense]
        .flatMap((group) => group.lines)
        .filter((line) => line.status !== 'mapped');
}
//# sourceMappingURL=budget.js.map